import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { DriverAssignmentService } from "@app/services/driverAssignment.service";
import { NavigationService } from "@app/services/navigation.service";
import { IVehicle } from "@interfaces/IVehicle";
import { AccountSettingsService, IAccountSettingsContent } from "@services/account-settings.service";
import * as angular from "angular";
import { IVideoStream } from "../models/ICameraChannel";
import { IVehicleOnlineStatus } from "../models/IVehicleOnlineStatus";
import { CameraFootageService } from "../services/camera-footage.service";
import { CameraStatusService } from "../services/camera-status.service";
import { CameraService } from "../services/camera.service";
import "./vehicleVideoCard.scss";

interface IComponentBindings {
    vehicle: IVehicle;
    hideTitle: boolean;
    requestVideoTime: Date;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    vehicle: IVehicle;
    vehicleId: number;

    hideTitle: boolean;
    requestVideoTime: Date;
    tabToShow: string;

    requestVideoEnabled: boolean;

    vehicleOnlineStatus: IVehicleOnlineStatus;

    checkedFunctionality: boolean;
    receivedOnlineStatus: boolean;

    currentTab: string;
    channelCount = 0;

    loading = false;

    accountSettings: IAccountSettingsContent

    get showRequestVideoTab(): boolean {
        return this.checkedFunctionality && this.receivedOnlineStatus && this.requestVideoEnabled && this.accountSettingsShowRequestVideo;
    }

    get showLiveVideoTab(): boolean {
        return this.checkedFunctionality 
            && this.receivedOnlineStatus 
            && this.vehicleOnlineStatus?.hasStreaming 
            && this.accountSettingsShowLiveView
            && this.doesVehicleHaveActiveChannels();
    }

    get accountSettingsShowLiveView(): boolean {
        return !this.accountSettings?.DisableLiveView?.Value;
    }

    get accountSettingsShowRequestVideo(): boolean {
        return !this.accountSettings?.DisableNonEventDownloads?.Value;
    }

    get disableLiveVideoTab(): boolean {
        return !this.vehicleOnlineStatus?.online;
    }

    static $inject = ["$scope", "cameraService", "cameraFootageService", "driverAssignmentService", "navigationService", "accountSettingsService", "cameraStatusService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly cameraService: CameraService,
        private readonly cameraFootageService: CameraFootageService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly navigationService: NavigationService,
        private readonly accountSettingsService: AccountSettingsService,
        private readonly cameraStatusService: CameraStatusService) {
    }

    $onInit(): void {
        this.renderTabs(true);
        this.tabToShow = this.navigationService.getRouteParam("tab");
        this.requestVideoTime = new Date(this.navigationService.getRouteParam("requestVideoTime"));
        this.$scope.$applyAsync();
    }

    $onChanges(): void {
        if (this.vehicleId) {
            this.loadVehicleFromId();
        } else if (this.vehicle) {
            this.getCameraChannels();
        }

        this.getAllAccountSettings();
    }

    getAllAccountSettings() {
        this.accountSettingsService.getAccountSettings().then(settings => {
            this.accountSettings = settings;
        });
    }

    onTabChange(tab: VTabComponent): void {
        this.currentTab = tab?.tabId;
        this.$scope.$applyAsync();
    }

    onVideoEventClick(requestVideoTime: Date): void {
        this.requestVideoTime = requestVideoTime;
    }

    onVehicleStatusChange(updatedVehicleOnlineStatus: IVehicleOnlineStatus): void {
        this.vehicleOnlineStatus = updatedVehicleOnlineStatus;
        this.receivedOnlineStatus = true;
    }

    doesVehicleHaveActiveChannels(): boolean {
        return this.vehicle?.videoChannels?.some(c => c.IsActive) || false;
    }

    // ---

    private renderTabs(initial: boolean = false): void {
        if (!this.vehicle?.Id || !this.vehicle.hasCamera) {
            return;
        }

        this.cameraService.getPeripherals(this.vehicle.Id).then(peripherals => {
            this.requestVideoEnabled = this.cameraFootageService.isFootageFunctionalityAvailable(peripherals);
            this.checkedFunctionality = true;
            this.getCameraChannels();
            this.$scope.$applyAsync();
        });
    }

    private loadVehicleFromId(): void {
        this.loading = true;
        const vehiclePromise = this.driverAssignmentService.getVehiclesWithDrivers({ includeLatestPlots: true, includePeripheralMappings: true });

        vehiclePromise
            .then(vehicles => {
                this.vehicle = vehicles.find(v => v.Id == this.vehicleId);

                this.navigationService.updateBreadcrumbNameResolver({ vehicleVideoNameKey: this.vehicle.ShortReg });

                this.renderTabs();

                this.getCameraChannels();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private getCameraChannels(): Promise<IVideoStream[] | void> {
        return this.cameraStatusService.getVideoChannels(this.vehicle.Id).then(channels => {
            this.vehicle.videoChannels = channels;

            this.channelCount = this.vehicle.videoChannels.filter(c => c.IsActive).length;
        });
    }
}

export class VehicleVideoCardComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<",
            vehicleId: "<",
            hideTitle: "<",
            requestVideoTime: "<?"
        };

        this.controller = ComponentController;
        this.template = require("./vehicleVideoCard.html");
    }
}
